import { isValidPhoneNumber } from "react-phone-number-input";
import { QUESTION_TYPE } from "./constants";

export const OPEN_TEXT_MAX_LENGTH = 1500;
// Same shape the API accepts (api/utils/survey_answer_types.normalize_email).
export const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const URL_HOST = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** "https://www.Bigfive-Test.com/tr" → "bigfive-test.com": the site a url question accepts. */
export function urlDomain(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  try {
    const host = new URL(raw.includes("://") ? raw : `https://${raw}`).hostname;
    return host.startsWith("www.") ? host.slice(4) : host;
  } catch (_) {
    return "";
  }
}

/** The web address as the API stores it (https:// added), or null when it isn't one or is on another
 * site than the question's urlDomain. Mirrors pivony-api survey_answer_types.normalize_url. */
export function normalizeUrlAnswer(value, domain) {
  const raw = String(value || "").trim();
  if (!raw || raw.length > 2000 || /\s/.test(raw)) return null;
  const withScheme = raw.includes("://") ? raw : `https://${raw}`;
  let parsed;
  try {
    parsed = new URL(withScheme);
  } catch (_) {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (!["http:", "https:"].includes(parsed.protocol) || !URL_HOST.test(host)) return null;
  const wanted = urlDomain(domain);
  if (wanted && host !== wanted && !host.endsWith(`.${wanted}`)) return null;
  return withScheme;
}
export const SHORT_TEXT_MAX_LENGTH = 200;
export const AUTO_ADVANCE_MS = 450;

export function isSteppedFlow(flowMode) {
  return flowMode !== "single_page";
}

export function supportsAutoAdvance(questionType) {
  return (
    questionType === QUESTION_TYPE.RATING ||
    questionType === QUESTION_TYPE.SINGLE_CHOICE
  );
}

export function isQuestionAnswered(question, answers) {
  const val = answers[question.id];
  if (val === undefined || val === null || val === "") return false;
  if (Array.isArray(val) && val.length === 0) return false;
  if (question.type === QUESTION_TYPE.LOCATION) {
    return !!(val.country && String(val.city || "").trim());
  }
  if (question.type === QUESTION_TYPE.VIDEO) return !!val.video_ref;
  return true;
}

/** False for an answer the API would refuse (a phone number that isn't a real one). Blank is valid. */
export function isAnswerValid(question, answers) {
  const val = answers[question.id];
  if (question.type === QUESTION_TYPE.PHONE && val) return isValidPhoneNumber(String(val));
  if (question.type === QUESTION_TYPE.EMAIL && val) return EMAIL_PATTERN.test(String(val).trim());
  if (question.type === QUESTION_TYPE.URL && val) return normalizeUrlAnswer(val, question.urlDomain) !== null;
  return true;
}

export function countAnsweredQuestions(questions, answers) {
  return (questions || []).filter((q) => isQuestionAnswered(q, answers)).length;
}

export function getTotalSteps(questionCount, hasContactStep) {
  return questionCount + (hasContactStep ? 1 : 0);
}

export function isContactStepIndex(stepIndex, questionCount) {
  return stepIndex >= questionCount;
}

export function findFirstInvalidQuestion(questions, answers) {
  for (const q of questions || []) {
    if (q.required && !isQuestionAnswered(q, answers)) return q;
    if (!isAnswerValid(q, answers)) return q;
  }
  return null;
}

export function getRatingKeyValues(question) {
  const scale = question.ratingScale || question.scale || "1-5";
  const ranges = {
    "1-5": { min: 1, max: 5 },
    nps: { min: 0, max: 10 },
  };
  const meta = ranges[scale] || ranges["1-5"];
  const values = [];
  for (let i = meta.min; i <= meta.max; i += 1) values.push(i);
  return values;
}

// Video answers: the question's min and max seconds.
export const DEFAULT_MIN_SECONDS = 10;
export const DEFAULT_MAX_SECONDS = 120;
const LONGEST_SECONDS = 600;

// Same rules as the API (survey_video.duration_limits).
export function videoDurationLimits(question) {
  const num = (v, d) => (Number.isFinite(Number(v)) && v !== null && v !== "" ? Math.trunc(Number(v)) : d);
  const max = Math.max(1, Math.min(num(question?.videoMaxSeconds, DEFAULT_MAX_SECONDS), LONGEST_SECONDS));
  const min = Math.max(0, Math.min(num(question?.videoMinSeconds, DEFAULT_MIN_SECONDS), max));
  return { min, max };
}

export function formatSeconds(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
